/*
 * Southern Air Trade — template runtime.
 *
 * A dependency-free reimplementation of the Claude Design component runtime that
 * the source file (Southern Air Trade.dc.html) was authored against. It reproduces
 * that runtime's semantics exactly so the ported markup renders unchanged:
 *
 *   {{ expr }}                  text + attribute interpolation
 *   expression grammar          identifier paths (a.b[c]), !, ==, !=, ===, !==,
 *                               parenthesised groups, numbers, quoted strings,
 *                               true / false / null / undefined
 *   <sc-if value="{{ x }}">     truthy gate
 *   <sc-for list="{{ xs }}"     iteration, exposing `as` and $index
 *        as="x">
 *   onClick / onInput / ...     event props resolved from the render values
 *   style-hover / style-active  generated pseudo-class rules (declarations get
 *                               !important, matching the original runtime)
 *   ref="{{ r }}"               object refs ({current}) and callback refs
 *   value / checked             coerced to '' / false when unresolved
 *
 * Rendering is a small vdom with in-place patching so form fields keep focus and
 * caret position across setState — the source app types into search, clearance
 * code, checkout and Prometheus inputs while re-rendering on every keystroke.
 */
(function (global) {
  'use strict';

  /* ------------------------------------------------------------------ expr */

  var IDENT_RE = /^[A-Za-z_$][A-Za-z0-9_$]*/;
  var NUMBER_RE = /^-?\d+(\.\d+)?$/;

  function parensWrapWhole(expr) {
    var depth = 0;
    for (var i = 0; i < expr.length - 1; i++) {
      if (expr[i] === '(') depth++;
      else if (expr[i] === ')') { depth--; if (depth === 0) return false; }
    }
    return true;
  }

  function findTopLevelEquality(expr) {
    var depth = 0;
    for (var i = 0; i < expr.length; i++) {
      var c = expr[i];
      if (c === '[' || c === '(') depth++;
      else if (c === ']' || c === ')') depth--;
      else if (depth === 0 && (c === '=' || c === '!') && expr[i + 1] === '=') {
        if (i > 0 && (expr[i - 1] === '=' || expr[i - 1] === '!')) continue;
        if (!expr.slice(0, i).trim()) continue;
        var op = expr[i + 2] === '=' ? c + '==' : c + '=';
        return { index: i, op: op };
      }
    }
    return null;
  }

  function resolvePath(vals, expr) {
    var head = expr.match(IDENT_RE);
    if (!head) return undefined;
    var cur = vals == null ? undefined : vals[head[0]];
    var i = head[0].length;
    while (i < expr.length) {
      if (expr[i] === '.') {
        var m = expr.slice(i + 1).match(IDENT_RE) || expr.slice(i + 1).match(/^\d+/);
        if (!m) return undefined;
        cur = cur == null ? undefined : cur[m[0]];
        i += 1 + m[0].length;
      } else if (expr[i] === '[') {
        var depth = 1, j = i + 1;
        while (j < expr.length && depth > 0) {
          if (expr[j] === '[') depth++;
          else if (expr[j] === ']') { depth--; if (depth === 0) break; }
          j++;
        }
        if (depth !== 0) return undefined;
        var key = resolve(vals, expr.slice(i + 1, j));
        cur = cur == null ? undefined : cur[key];
        i = j + 1;
      } else {
        return undefined;
      }
    }
    return cur;
  }

  function resolve(vals, src) {
    var expr = String(src).trim();
    if (!expr) return undefined;
    if (expr[0] === '(' && expr[expr.length - 1] === ')' && parensWrapWhole(expr)) {
      return resolve(vals, expr.slice(1, -1));
    }
    var eq = findTopLevelEquality(expr);
    if (eq) {
      var lv = resolve(vals, expr.slice(0, eq.index));
      var rv = resolve(vals, expr.slice(eq.index + eq.op.length));
      switch (eq.op) {
        case '===': return lv === rv;
        case '!==': return lv !== rv;
        case '==': return lv == rv; // eslint-disable-line eqeqeq
        default: return lv != rv;   // eslint-disable-line eqeqeq
      }
    }
    if (expr[0] === '!') return !resolve(vals, expr.slice(1));
    if (expr === 'true') return true;
    if (expr === 'false') return false;
    if (expr === 'null') return null;
    if (expr === 'undefined') return undefined;
    if (NUMBER_RE.test(expr)) return Number(expr);
    if (expr.length >= 2 && (expr[0] === '"' || expr[0] === "'") && expr[expr.length - 1] === expr[0]) {
      return expr.slice(1, -1);
    }
    return resolvePath(vals, expr);
  }

  function compileAttr(raw) {
    var whole = raw.match(/^\s*\{\{([\s\S]+?)\}\}\s*$/);
    if (whole) {
      var p = whole[1];
      return function (vals) { return resolve(vals, p); };
    }
    if (raw.indexOf('{{') !== -1) {
      var parts = raw.split(/\{\{([\s\S]+?)\}\}/g);
      return function (vals) {
        var out = '';
        for (var i = 0; i < parts.length; i++) {
          if (i & 1) { var v = resolve(vals, parts[i]); out += v == null ? '' : v; }
          else out += parts[i];
        }
        return out;
      };
    }
    return function () { return raw; };
  }

  /* ------------------------------------------------------- pseudo-class sheet */

  var pseudoSheetEl = null;
  var pseudoCache = Object.create(null);
  var pseudoN = 0;

  function scanUnquotedUrl(css, i) {
    if ((css[i] !== 'u' && css[i] !== 'U') || css.slice(i, i + 4).toLowerCase() !== 'url(' ||
        /[a-z0-9_-]/i.test(css[i - 1] || '')) return -1;
    var j = i + 4;
    while (j < css.length && /\s/.test(css[j])) j++;
    if (css[j] === '"' || css[j] === "'") return -1;
    while (j < css.length && css[j] !== ')') { if (css[j] === '\\') j++; j++; }
    return j < css.length ? j + 1 : css.length;
  }

  function importantify(css) {
    var decls = [], start = 0, depth = 0, quote = '';
    for (var i = 0; i < css.length; i++) {
      var c = css[i];
      if (quote) { if (c === '\\') i++; else if (c === quote) quote = ''; }
      else if (c === "'" || c === '"') quote = c;
      else if (c === '(') depth++;
      else if (c === ')') depth = Math.max(0, depth - 1);
      else if (c === ';' && depth === 0) { decls.push(css.slice(start, i)); start = i + 1; }
      else { var end = scanUnquotedUrl(css, i); if (end !== -1) i = end - 1; }
    }
    decls.push(css.slice(start));
    return decls.map(function (d) { return d.trim(); }).filter(Boolean)
      .map(function (d) { return /!\s*important$/i.test(d) ? d : d + ' !important'; }).join(';');
  }

  function pseudoClass(pseudo, css) {
    var k = pseudo + '|' + css;
    if (pseudoCache[k]) return pseudoCache[k];
    if (!pseudoSheetEl) {
      pseudoSheetEl = document.createElement('style');
      pseudoSheetEl.setAttribute('data-sat-pseudo', '');
      document.head.appendChild(pseudoSheetEl);
    }
    var cls = 'scp' + (pseudoN++).toString(36);
    var isEl = pseudo === 'before' || pseudo === 'after';
    var sel = isEl ? '.' + cls + '::' + pseudo : '.' + cls + ':' + pseudo;
    try {
      pseudoSheetEl.sheet.insertRule(sel + '{' + (isEl ? css : importantify(css)) + '}',
        pseudoSheetEl.sheet.cssRules.length);
    } catch (e) { /* malformed declaration — matches the original runtime's tolerance */ }
    pseudoCache[k] = cls;
    return cls;
  }

  /* ----------------------------------------------------------------- compile */

  // Tags the HTML parser refuses to keep <sc-for>/<sc-if> inside (foster parenting).
  // The source runtime solved this the same way, by wrapping before parsing.
  var RAW_WRAP = ['table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption', 'select'];
  var RAW_UNWRAP = {};
  RAW_WRAP.forEach(function (t) { RAW_UNWRAP['sc-raw-' + t] = t; });

  function preParse(html) {
    RAW_WRAP.forEach(function (t) {
      html = html.replace(new RegExp('<' + t + '(?=[\\s>])', 'gi'), '<sc-raw-' + t)
                 .replace(new RegExp('<' + t + '>', 'gi'), '<sc-raw-' + t + '>')
                 .replace(new RegExp('</' + t + '\\s*>', 'gi'), '</sc-raw-' + t + '>');
    });
    return html;
  }

  var SVG_TAGS = {
    svg: 1, path: 1, circle: 1, ellipse: 1, g: 1, defs: 1, rect: 1, line: 1, polyline: 1,
    polygon: 1, text: 1, tspan: 1, textPath: 1, pattern: 1, mask: 1, clipPath: 1, use: 1,
    image: 1, marker: 1, symbol: 1, filter: 1, animate: 1, animateMotion: 1,
    animateTransform: 1, mpath: 1, set: 1, foreignObject: 1, linearGradient: 1,
    radialGradient: 1, stop: 1, feGaussianBlur: 1, feOffset: 1, feMerge: 1,
    feMergeNode: 1, feColorMatrix: 1, feTurbulence: 1, feDisplacementMap: 1, feBlend: 1
  };
  // Attributes whose camelCase must survive into the SVG DOM.
  var SVG_CASE = {};
  ['attributeName', 'attributeType', 'repeatCount', 'repeatDur', 'calcMode', 'keyTimes',
   'keySplines', 'keyPoints', 'viewBox', 'preserveAspectRatio', 'patternUnits',
   'patternContentUnits', 'patternTransform', 'gradientUnits', 'gradientTransform',
   'clipPath', 'clipPathUnits', 'maskUnits', 'maskContentUnits', 'markerWidth',
   'markerHeight', 'markerUnits', 'refX', 'refY', 'startOffset', 'textLength',
   'lengthAdjust', 'spreadMethod', 'stdDeviation', 'baseFrequency', 'numOctaves',
   'diffuseConstant', 'surfaceScale', 'specularConstant', 'specularExponent',
   'pathLength', 'requiredFeatures', 'systemLanguage']
    .forEach(function (a) { SVG_CASE[a.toLowerCase()] = a; });

  var EVENT_MAP = {
    onclick: 'click', onchange: 'change', oninput: 'input', onsubmit: 'submit',
    onkeydown: 'keydown', onkeyup: 'keyup', onkeypress: 'keypress',
    onmousedown: 'mousedown', onmouseup: 'mouseup', onmouseenter: 'mouseenter',
    onmouseleave: 'mouseleave', onfocus: 'focus', onblur: 'blur',
    ondoubleclick: 'dblclick', oncontextmenu: 'contextmenu', onmousemove: 'mousemove',
    onmouseover: 'mouseover', onmouseout: 'mouseout', onpointerdown: 'pointerdown',
    onpointerup: 'pointerup', onpointermove: 'pointermove', onpointerenter: 'pointerenter',
    onpointerleave: 'pointerleave', onpointercancel: 'pointercancel',
    onpointerover: 'pointerover', onpointerout: 'pointerout',
    ontouchstart: 'touchstart', ontouchend: 'touchend', ontouchmove: 'touchmove',
    ontouchcancel: 'touchcancel', ondragstart: 'dragstart', ondragend: 'dragend',
    ondragenter: 'dragenter', ondragleave: 'dragleave', ondragover: 'dragover',
    onanimationstart: 'animationstart', onanimationend: 'animationend',
    onanimationiteration: 'animationiteration', ontransitionend: 'transitionend',
    onload: 'load', onerror: 'error', onended: 'ended', onplay: 'play', onpause: 'pause',
    onscroll: 'scroll', onwheel: 'wheel', onreset: 'reset', onselect: 'select',
    ontoggle: 'toggle'
  };

  var ATTR_ALIAS = { classname: 'class', htmlfor: 'for', tabindex: 'tabindex', autofocus: 'autofocus' };

  function compileNode(node) {
    if (node.nodeType === 3) { // text
      var txt = node.nodeValue || '';
      if (txt.indexOf('{{') === -1) {
        if (!txt.trim() && txt.indexOf(' ') === -1) return null;
        return function () { return { t: 0, v: txt }; };
      }
      var parts = txt.split(/\{\{([\s\S]+?)\}\}/g);
      return function (vals) {
        var out = '';
        for (var i = 0; i < parts.length; i++) {
          if (i & 1) {
            var v = resolve(vals, parts[i]);
            if (v === undefined || v === null || typeof v === 'boolean') continue;
            out += String(v);
          } else out += parts[i];
        }
        return { t: 0, v: out };
      };
    }
    if (node.nodeType === 8) return null;   // comment — dropped, as in the source runtime
    if (node.nodeType !== 1) return null;

    var tag = node.localName;

    if (tag === 'sc-for') {
      var listGet = compileAttr(node.getAttribute('list') || '');
      var asName = node.getAttribute('as') || 'item';
      var kidsF = compileChildren(node);
      return function (vals) {
        var list = listGet(vals);
        if (!Array.isArray(list)) list = [];
        var out = [];
        for (var i = 0; i < list.length; i++) {
          var sub = Object.create(vals);
          sub[asName] = list[i];
          sub.$index = i;
          for (var j = 0; j < kidsF.length; j++) {
            var n = kidsF[j](sub);
            if (n != null) push(out, n);
          }
        }
        return out;
      };
    }

    if (tag === 'sc-if') {
      var valGet = compileAttr(node.getAttribute('value') || '');
      var kidsI = compileChildren(node);
      return function (vals) {
        if (!valGet(vals)) return [];
        var out = [];
        for (var j = 0; j < kidsI.length; j++) {
          var n = kidsI[j](vals);
          if (n != null) push(out, n);
        }
        return out;
      };
    }

    var realTag = RAW_UNWRAP[tag] || node.tagName; // tagName preserves SVG camelCase
    if (RAW_UNWRAP[tag]) realTag = RAW_UNWRAP[tag];
    else if (!SVG_TAGS[node.tagName]) realTag = tag;

    var attrs = [];      // [name, getter]
    var events = [];     // [eventName, getter]
    var pseudos = [];    // class names, static
    var refGet = null;
    for (var a = 0; a < node.attributes.length; a++) {
      var name = node.attributes[a].name;
      var value = node.attributes[a].value;
      var lower = name.toLowerCase();
      if (lower.indexOf('style-') === 0 && lower !== 'style') {
        pseudos.push(pseudoClass(lower.slice(6), value));
        continue;
      }
      if (lower === 'ref') { refGet = compileAttr(value); continue; }
      if (lower.indexOf('on') === 0 && EVENT_MAP[lower]) {
        events.push([EVENT_MAP[lower], compileAttr(value)]);
        continue;
      }
      var outName = ATTR_ALIAS[lower] || (SVG_CASE[lower] || name);
      attrs.push([outName, compileAttr(value), lower]);
    }
    var kids = compileChildren(node);

    return function (vals) {
      var props = {}, evts = null, i, v;
      for (i = 0; i < attrs.length; i++) {
        v = attrs[i][1](vals);
        var key = attrs[i][2];
        if ((key === 'value' || key === 'checked') && v === undefined) {
          v = key === 'checked' ? false : '';
        }
        props[attrs[i][0]] = v;
      }
      if (pseudos.length) {
        props['class'] = [props['class'], pseudos.join(' ')].filter(Boolean).join(' ');
      }
      for (i = 0; i < events.length; i++) {
        v = events[i][1](vals);
        if (typeof v === 'function') { (evts || (evts = {}))[events[i][0]] = v; }
      }
      var children = [];
      for (i = 0; i < kids.length; i++) {
        var n = kids[i](vals);
        if (n != null) push(children, n);
      }
      return {
        t: 1, tag: realTag, props: props, evts: evts, children: children,
        ref: refGet ? refGet(vals) : null
      };
    };
  }

  function push(arr, n) {
    if (Array.isArray(n)) { for (var i = 0; i < n.length; i++) push(arr, n[i]); }
    else arr.push(n);
  }

  function compileChildren(node) {
    var out = [];
    for (var i = 0; i < node.childNodes.length; i++) {
      var f = compileNode(node.childNodes[i]);
      if (f) out.push(f);
    }
    return out;
  }

  function compileTemplate(html) {
    var tpl = document.createElement('template');
    tpl.innerHTML = preParse(html);
    var builders = compileChildren(tpl.content);
    return function (vals) {
      var out = [];
      for (var i = 0; i < builders.length; i++) {
        var n = builders[i](vals || {});
        if (n != null) push(out, n);
      }
      return out;
    };
  }

  /* ------------------------------------------------------------------- patch */

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var XLINK_NS = 'http://www.w3.org/1999/xlink';

  function createEl(vnode, inSvg) {
    var svg = inSvg || vnode.tag === 'svg';
    var el = svg ? document.createElementNS(SVG_NS, vnode.tag) : document.createElement(vnode.tag);
    applyProps(el, {}, vnode.props, svg);
    applyEvents(el, vnode.evts);
    applyRef(el, vnode.ref);
    for (var i = 0; i < vnode.children.length; i++) {
      el.appendChild(createNode(vnode.children[i], svg && vnode.tag !== 'foreignObject'));
    }
    if (vnode.props.autofocus) {
      setTimeout(function () { try { el.focus(); } catch (e) {} }, 0);
    }
    return el;
  }

  function createNode(vnode, inSvg) {
    if (vnode.t === 0) return document.createTextNode(vnode.v);
    return createEl(vnode, inSvg);
  }

  function applyRef(el, ref) {
    if (!ref) return;
    if (typeof ref === 'function') ref(el);
    else if (typeof ref === 'object') ref.current = el;
  }

  var PROP_ATTRS = { value: 1, checked: 1, selected: 1, muted: 1 };

  function applyProps(el, oldP, newP, svg) {
    var k;
    for (k in oldP) {
      if (!(k in newP)) {
        if (PROP_ATTRS[k]) { try { el[k] = k === 'value' ? '' : false; } catch (e) {} }
        el.removeAttribute(k);
      }
    }
    for (k in newP) {
      var v = newP[k];
      if (oldP[k] === v && !PROP_ATTRS[k]) continue;
      if (PROP_ATTRS[k]) {
        // Set as a property so the field's own edits are respected, and only when
        // it actually differs — otherwise the caret jumps on every keystroke.
        try { if (el[k] !== v) el[k] = v; } catch (e) {}
        if (k === 'value') continue;
      }
      if (v === false || v === null || v === undefined) { el.removeAttribute(k); continue; }
      if (v === true) { el.setAttribute(k, ''); continue; }
      if (k === 'xlink:href') el.setAttributeNS(XLINK_NS, 'href', v);
      else el.setAttribute(k, v);
    }
  }

  function applyEvents(el, evts) {
    var bound = el.__satEvts;
    if (!bound) {
      bound = el.__satEvts = {};
      el.__satHandlers = {};
    }
    var name;
    for (name in bound) {
      if (!evts || !(name in evts)) {
        el.removeEventListener(name, bound[name]);
        delete bound[name];
        delete el.__satHandlers[name];
      }
    }
    if (!evts) return;
    for (name in evts) {
      el.__satHandlers[name] = evts[name];
      if (!bound[name]) {
        bound[name] = (function (n) {
          return function (ev) {
            var h = el.__satHandlers[n];
            if (h) h.call(el, ev);
          };
        })(name);
        el.addEventListener(name, bound[name]);
      }
    }
  }

  function sameType(a, b) {
    if (a.t !== b.t) return false;
    if (a.t === 0) return true;
    return a.tag === b.tag;
  }

  function patch(parent, oldList, newList, inSvg) {
    var i, n = Math.max(oldList.length, newList.length);
    var domIndex = 0;
    for (i = 0; i < n; i++) {
      var o = oldList[i], nv = newList[i];
      var node = parent.childNodes[domIndex];
      if (o === undefined && nv !== undefined) {
        parent.appendChild(createNode(nv, inSvg));
        domIndex++;
      } else if (nv === undefined && o !== undefined) {
        if (node) parent.removeChild(node);
      } else if (!sameType(o, nv)) {
        var fresh = createNode(nv, inSvg);
        if (node) parent.replaceChild(fresh, node);
        else parent.appendChild(fresh);
        domIndex++;
      } else if (nv.t === 0) {
        if (o.v !== nv.v && node) node.nodeValue = nv.v;
        domIndex++;
      } else {
        var svg = inSvg || nv.tag === 'svg';
        applyProps(node, o.props, nv.props, svg);
        applyEvents(node, nv.evts);
        if (nv.ref) applyRef(node, nv.ref);
        patch(node, o.children, nv.children, svg && nv.tag !== 'foreignObject');
        domIndex++;
      }
    }
    // Trim any trailing DOM the vdom no longer accounts for.
    while (parent.childNodes.length > newList.length) {
      parent.removeChild(parent.lastChild);
    }
  }

  /* ------------------------------------------------------------------- mount */

  function Logic(props) {
    this.props = props || {};
    this.state = {};
  }
  Logic.prototype.setState = function (update, cb) {
    var next = typeof update === 'function' ? update(this.state) : update;
    if (next) for (var k in next) this.state[k] = next[k];
    this.__schedule(cb);
  };
  Logic.prototype.forceUpdate = function (cb) { this.__schedule(cb); };
  Logic.prototype.componentDidMount = function () {};
  Logic.prototype.componentDidUpdate = function () {};
  Logic.prototype.componentWillUnmount = function () {};
  Logic.prototype.renderVals = function () { return {}; };
  Logic.prototype.__schedule = function (cb) {
    var self = this;
    if (cb) (this.__cbs || (this.__cbs = [])).push(cb);
    if (this.__pending) return;
    this.__pending = true;
    (global.requestAnimationFrame || function (f) { setTimeout(f, 16); })(function () {
      self.__pending = false;
      self.__render();
      var cbs = self.__cbs; self.__cbs = null;
      if (cbs) for (var i = 0; i < cbs.length; i++) { try { cbs[i](); } catch (e) { console.error(e); } }
      try { self.componentDidUpdate(); } catch (e) { console.error(e); }
    });
  };

  function mount(opts) {
    var host = opts.host;
    var render = compileTemplate(opts.template);
    var logic = new opts.Logic(opts.props || {});
    var prev = [];
    // The mount point owns nothing but the app: anything the server put inside it
    // (the no-script notice) is replaced the moment the runtime takes over.
    while (host.firstChild) host.removeChild(host.firstChild);

    logic.__render = function () {
      var vals;
      try {
        vals = Object.assign({}, logic.props, logic.renderVals() || {});
      } catch (e) {
        console.error('[sat] renderVals():', e);
        return;
      }
      var next;
      try { next = render(vals); }
      catch (e) { console.error('[sat] render:', e); return; }
      patch(host, prev, next, false);
      prev = next;
    };

    logic.__render();
    try { logic.componentDidMount(); } catch (e) { console.error(e); }
    global.addEventListener('beforeunload', function () {
      try { logic.componentWillUnmount(); } catch (e) {}
    });
    return logic;
  }

  global.SAT = {
    mount: mount,
    Logic: Logic,
    resolve: resolve,
    compileTemplate: compileTemplate,
    // The source logic uses React.createRef() exactly once (heroVideoRef).
    React: { createRef: function () { return { current: null }; } }
  };
})(window);
